import { Component, OnInit, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Delivery, DeliveryRepports } from '../../bo/delivery';
import { Projet } from '../../bo/projet';
import { Tir } from '../../bo/tir';
import { DeliveriesService } from '../../services/deliveries.service';
import { FonctionsService } from '../../services/fonctions-partagees.service';
import { HttpService } from '../../services/http.service';
import { TraductionService } from '../../services/traduction.service';
import { SurveyService } from 'src/app/services/survey.service';
import { FileDownloadService } from 'src/app/services/fileDownload.service';
import { CustomSelectOption } from '../../shared/components/custom-select/custom-select.component';

enum ReportType { DELIVERIESLOG = 1, FEEDBACK = 2 };

class Report {
  constructor(public name: string, public type: ReportType) { }
}

/**
 * Modale « Reports » du site (rapports de livraisons / feedback), extraite de MesProjetsComponent
 * pour être ouverte aussi depuis /site. Ouverte via BsModalService avec `siteId` et `tabProjets` (projets avec leurs tirs).
 */
@Component({
  selector: 'app-site-reports-modale',
  templateUrl: './site-reports-modale.component.html',
  styleUrls: ['./site-reports-modale.component.scss'],
  standalone: false
})
export class SiteReportsModaleComponent implements OnInit {
  siteId: number;
  tabProjets: Projet[] = [];

  readonly modalRef = inject(BsModalRef);
  private readonly http = inject(HttpService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly translate = inject(TranslateService);
  private readonly traductionService = inject(TraductionService);
  private readonly deliveriesService = inject(DeliveriesService);
  private readonly surveyService = inject(SurveyService);
  private readonly fileDownloadService = inject(FileDownloadService);

  ReportType = ReportType;
  tabProjetsSelected = signal<Projet[]>([]);
  reportTemplates = signal<Report[]>([]);
  selectedReport = signal<Report>(null);
  deliveries = signal<Delivery[]>([]);
  deliveriesDates = signal<Delivery[]>([]);
  tirsBySurveyAnswer = signal<Tir[]>([]);
  tirsBySurveyAnswerFiltered = signal<Tir[]>([]);
  allTirId = signal<number[]>([]);
  datesSelected = signal<Date[]>([]);
  isResume = signal<boolean>(false);

  startDate = signal<Date>(null);
  startTime = signal<string>(null);
  endDate = signal<Date>(null);
  endTime = signal<string>(null);
  duration = signal<string>('');
  combinedStartDateTime = signal<Date>(null);
  combinedEndDateTime = signal<Date>(null);
  invalideDate = signal<boolean>(false);
  hours = signal<string[]>([]);
  startTimeOptions = signal<CustomSelectOption[]>([]);
  endTimeOptions = signal<CustomSelectOption[]>([]);

  ngOnInit() {
    this.hours.set(Array.from({length: 24}, (_, i) =>
      i.toString().padStart(2, '0')
    ));
    this.createTimeOptions();
    this.tabProjetsSelected.set(this.tabProjets ?? []);
    this.allTirId.set(this.getAllTirIds(this.tabProjetsSelected()));
    this.populateReportList();
  }

  private populateReportList() {
    this.traductionService.translate.get(['RAPPORT_TIR.BLAST_REPORT', 'RAPPORT_TIR.DRILL_REPORT', 'RAPPORT_TIR.DELIVERIES_REPORT', 'RAPPORT_TIR.FEEDBACK']).subscribe(traduction => {
      const templates = [];
      templates.push(new Report(traduction['RAPPORT_TIR.DELIVERIES_REPORT'], ReportType.DELIVERIESLOG));
      templates.push(new Report(traduction['RAPPORT_TIR.FEEDBACK'], ReportType.FEEDBACK));
      this.reportTemplates.set(templates);
      this.selectedReport.set(templates[0]);
    });
  }

  protected downloadRepports() {
    if (this.selectedReport().type === ReportType.DELIVERIESLOG) {
      this.downloadRepportsDeliveries();
    }
    if (this.selectedReport().type === ReportType.FEEDBACK) {
      this.downloadRepportsFeedback();
    }
    this.modalRef.hide();
  }

  onReportTypeChange(newReport: Report) {
    this.selectedReport.set(newReport);

    // Vider tous les champs de date et heure
    this.startDate.set(null);
    this.startTime.set(null);
    this.endDate.set(null);
    this.endTime.set(null);
    this.duration.set('');
    this.invalideDate.set(false);
    this.datesSelected.set([]);

    // Vider les données de deliveries et feedback
    this.deliveries.set([]);
    this.deliveriesDates.set([]);
    this.tirsBySurveyAnswer.set([]);
    this.tirsBySurveyAnswerFiltered.set([]);
  }

  protected downloadRepportsFeedback() {
    if (this.tirsBySurveyAnswerFiltered().length === 0) return;
      const tirsIds = this.tirsBySurveyAnswerFiltered().map(tir => tir.tirId);
      const tirsIdParams = tirsIds.map(id => `tirsId=${id}`).join('&');
      const url = `reports/${this.siteId}/reports-feedback?${tirsIdParams}`;
      this.fileDownloadService.downloadFile(url, 'feedback.csv');
  }

  protected downloadRepportsDeliveries() {
    if (this.deliveriesDates().length === 0) return;
    this.fonctionsService.loading_screen(true);
    let deliveryRepports = new DeliveryRepports(this.isResume());
    this.deliveriesDates().forEach((d) => {
      deliveryRepports.deliveryIds.push(d.deliveryId);
    });
    this.deliveriesDates.set([]);
    var today = new Date();
    var date = today.getDate() + '/' + (today.getMonth() + 1) + '/' + today.getFullYear();
    this.http.downloadFilePost(`Reports/${this.siteId}/getzip`, deliveryRepports).subscribe((resultat: any) => {
      const blob = new Blob([resultat], { type: 'base64' });
      const url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = "Delivery_reports_" + date + ".zip";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
        this.fonctionsService.loading_screen(false);
      }, 100);
    }, () => {
      this.fonctionsService.loading_screen(false);
      this.translate.get('GLOBAL.ERREUR_RECUP_DONNEES').subscribe(trad => {
        this.fonctionsService.launch_toast(false, false, trad)
      });
    })
  }

  private formatDeliveryTime(delivery: Delivery): Delivery {
    let newDelivery = new Delivery();
    let newStartTime = new Date(Date.parse(delivery.startTime)).toLocaleString();
    newDelivery.startTime = newStartTime;
    newDelivery.deliveryId = delivery.deliveryId;
    newDelivery.tirId = delivery.tirId;
    newDelivery.name = delivery.name;
    return newDelivery;
  }

  protected onChangeProjects(event: Projet[] | null) {
    this.tabProjetsSelected.set(event || []);
    this.allTirId.set(this.getAllTirIds(this.tabProjetsSelected()));

    if (!this.selectedReport()) return;

    if(this.selectedReport()?.type === ReportType.DELIVERIESLOG) {

      if(this.allTirId().length > 0) {
        this.deliveriesDates.set(this.updateDeliveriesByDateAndByProjects(this.deliveries(), this.datesSelected()));
      }
      else{
        this.deliveriesDates.set(this.deliveries() || []);
      }

      this.deliveriesDates.set(this.deliveriesDates().map(item => ({
            ...item,
            startTimeFormatted: this.formatDeliveryName(item.startTime, item.name || ''),
          })));
    }
    if(this.selectedReport()?.type === ReportType.FEEDBACK) {
      if(this.allTirId().length > 0) {
        this.tirsBySurveyAnswerFiltered.set(this.updateFeedbackAnswerByDateAndByProjects(this.tirsBySurveyAnswer(), this.datesSelected()));
      }
      else{
        this.tirsBySurveyAnswerFiltered.set(this.tirsBySurveyAnswer() || []);
      }

        this.tirsBySurveyAnswerFiltered.set(this.tirsBySurveyAnswerFiltered().map(item => {
          const newItem = Object.assign(new Tir(), item);
          (newItem as any).tirsName = this.formatDeliveryName(item.dateTirPrevisionnelle?.toString() || '', item.nom || '');
          return newItem;
        }));

    }
  }

  private getAllTirIds(projects: Projet[]): number[] {
    let arrayTirId = [];

    projects.forEach((p: Projet) => {
      (p.tirs ?? []).forEach((t: Tir) => {
        if (!arrayTirId.includes(t.tirId)) {
          arrayTirId.push(t.tirId);
        }
      })
    })
    return arrayTirId;
  }

  private updateDeliveriesByDateAndByProjects(deliveries: Delivery[] | null, dates: Date[] | null): Delivery[] {
    let updatedDeliveries: Delivery[] = [];
    if (!deliveries || !Array.isArray(deliveries) || !dates || !Array.isArray(dates) || dates.length < 2) return [];

    deliveries.forEach((delivery) => {
      if (!delivery || !delivery.startTime) return;

      const deliveryDate = new Date(delivery.startTime);

      if (isNaN(deliveryDate.getTime())) return;

      const startDate = new Date(dates[0]);
      const endDate = new Date(dates[1]);

      const deliveryStartTime = new Date(deliveryDate).setHours(0, 0, 1);
      const deliveryEndTime = new Date(deliveryDate).setHours(23, 59, 59);
      const startDateTime = startDate.setHours(0, 0, 1);
      const endDateTime = endDate.setHours(23, 59, 59);

      if (deliveryEndTime >= startDateTime && deliveryStartTime <= endDateTime) {
        if (this.allTirId().includes(delivery.tirId)) {
          updatedDeliveries.push(this.formatDeliveryTime(delivery));
        }
      }
    });
    return updatedDeliveries;
  }

  private updateFeedbackAnswerByDateAndByProjects(tirsBySurveyAnswers: Tir[] | null, dates: Date[] | null): Tir[] {
    let updatedSurveyAnswers: Tir[] = [];
    if (!tirsBySurveyAnswers || !Array.isArray(tirsBySurveyAnswers) || !dates || !Array.isArray(dates) || dates.length < 2) return [];

    tirsBySurveyAnswers.forEach((tir) => {
      if (!tir || !tir.dateTirPrevisionnelle) return;

      const answerDate = new Date(tir.dateTirPrevisionnelle);

      if (isNaN(answerDate.getTime())) return;

      const startDate = new Date(dates[0]);
      const endDate = new Date(dates[1]);

      const answerStartTime = new Date(answerDate).setHours(0, 0, 1);
      const answerEndTime = new Date(answerDate).setHours(23, 59, 59);
      const startDateTime = startDate.setHours(0, 0, 1);
      const endDateTime = endDate.setHours(23, 59, 59);

      if (answerEndTime >= startDateTime && answerStartTime <= endDateTime) {
        if (this.allTirId().includes(tir.tirId)) {
          updatedSurveyAnswers.push(tir);
        }
      }
    });

    return updatedSurveyAnswers;
  }

  onDateTimeChange(newValue: any = null, type: 'start_date' | 'end_date' = null) {
    if( type === 'start_date') {
      this.startDate.set(newValue);
    }
    if( type === 'end_date') {
      this.endDate.set(newValue);
    }

    if (this.startDate() && this.endDate() && this.startTime() && this.startDate() > this.endDate()) {
      this.duration.set('GLOBAL.INVALIDE_DATE');
      this.invalideDate.set(true);
      return;
    }

    const startTimeSelected = this.startTime() + ':00';
    const endTimeSelected = this.endTime() + ':00';

    this.combinedStartDateTime.set(this.combineDateAndTime(this.startDate(), startTimeSelected));
    this.combinedEndDateTime.set(this.combineDateAndTime(this.endDate(), endTimeSelected));

    const diffMs = this.combinedEndDateTime().getTime() - this.combinedStartDateTime().getTime();
    if (diffMs < 0) {
      this.duration.set('GLOBAL.INVALIDE_DATE');
      this.invalideDate.set(true);
      return;
    }

    this.datesSelected.set([this.combinedStartDateTime(), this.combinedEndDateTime()]);

    if(this.selectedReport()?.type === ReportType.DELIVERIESLOG) {
      this.deliveriesDates.set([]);
      this.deliveries.set([]);
      if (this.combinedStartDateTime() && this.combinedEndDateTime()) {
        this.deliveriesService.getDeliveriesBySiteId(this.siteId, this.combinedStartDateTime(), this.combinedEndDateTime())
          .subscribe((res: Delivery[]) => {
            res = res.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
            this.deliveries.set(res);

            if(this.allTirId().length > 0)
            {
              this.deliveriesDates.set(this.updateDeliveriesByDateAndByProjects(this.deliveries(), this.datesSelected()));
            }
            else
            {
              this.deliveriesDates.set(this.deliveries());
            }

            setTimeout(() => {
              this.deliveriesDates.set(this.deliveriesDates().map(item => ({
                ...item,
                startTimeFormatted: this.formatDeliveryName(item.startTime, item.name || ''),
              })));
            });
          });
      }

    }
    if(this.selectedReport()?.type === ReportType.FEEDBACK) {
      this.surveyService.getSurveyAnswersBySite(this.siteId, this.combinedStartDateTime(), this.combinedEndDateTime())
      .subscribe((res: Tir[]) => {
        this.tirsBySurveyAnswer.set(res);

        if(this.allTirId().length > 0) {
          this.tirsBySurveyAnswerFiltered.set(this.updateFeedbackAnswerByDateAndByProjects(this.tirsBySurveyAnswer(), this.datesSelected()));
        }
        else{
          this.tirsBySurveyAnswerFiltered.set(this.tirsBySurveyAnswer());
        }

        setTimeout(() => {
          this.tirsBySurveyAnswerFiltered.set(this.tirsBySurveyAnswerFiltered().map(item => {
            const newItem = Object.assign(new Tir(), item);
            (newItem as any).tirsName = this.formatDeliveryName(item.dateTirPrevisionnelle?.toString() || '', item.nom || '');
            return newItem;
          }));
        });
      });

    }

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      this.duration.set(`${diffDays}j ${diffHours}h ${diffMinutes}min`);
      this.invalideDate.set(false);

  }

  createTimeOptions() {
    this.startTimeOptions.set([
      { value: '', label: this.translate.instant('GLOBAL.HOUR') },
      ...this.hours().map(hour => ({
        value: hour,
        label: `${hour}:00`
      }))
    ]);

    this.endTimeOptions.set([
      { value: '', label: this.translate.instant('GLOBAL.HOUR') },
      ...this.hours().map(hour => ({
        value: hour,
        label: `${hour}:00`
      }))
    ]);
  }

  private combineDateAndTime(date: Date, time?: string): Date {
    if (!date) {
      return new Date();
    }

    const result = new Date(date);

    if (time) {
      const timeParts = time.split(':');
      if (timeParts.length >= 2) {
        const hours = parseInt(timeParts[0], 10) || 0;
        const minutes = parseInt(timeParts[1], 10) || 0;
        result.setHours(hours, minutes, 0, 0);
      }
    } else {
      result.setHours(0, 0, 0, 0);
    }

    return result;
  }

  formatDeliveryName(startDate: string, name: string): string {
    if (!startDate || !name) {
      return '';
    }

    const date = this.parseDate(startDate);

    if (isNaN(date.getTime())) {
      console.warn('Date invalide dans formatDeliveryName:', startDate);

      return name;
    }

    const dd = date.getDate().toString().padStart(2, '0');
    const MM = (date.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = date.getFullYear();
    const HH = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    const nameWithoutTime = name.replace(/_?\d{2}:\d{2}$/, '');

    return `${nameWithoutTime}_${dd}_${MM}_${yyyy}_${HH}h${mm}`;
  }

  parseDate(dateString: string): Date {
    if (dateString.includes('T') && (dateString.includes('Z') || dateString.includes('+'))) {
      return new Date(dateString);
    }

    const [datePart, timePart] = dateString.split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, seconds);
  }
}
